import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Lock, ShieldCheck, EyeOff, Globe } from "lucide-react";

const cards = [
  {
    icon: Lock,
    title: "End-to-End Encryption",
    text: "Your data is encrypted in transit (TLS 1.3) and at rest (AES-256). Even our own engineers cannot access or decrypt your message contents.",
  },
  {
    icon: ShieldCheck,
    title: "Official API Verification",
    text: "We strictly use Official Google OAuth, Microsoft Graph API, and Meta Cloud API. We never use risky 'scrapers' or bots that endanger your account security.",
  },
  {
    icon: EyeOff,
    title: "Zero-Training Guarantee",
    text: "Your internal communications are never used to train our public AI models. Your proprietary data stays isolated and yours.",
  },
  {
    icon: Globe,
    title: "GDPR & CCPA Compliant",
    text: "Full compliance with global data privacy laws. You retain full ownership of your data and can request a permanent deletion log at any time.",
  },
];

export const SecurityComplianceSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      <div className="absolute inset-0 bg-[#0A0F1C]/50" />
      <div className="container relative z-10" ref={ref}>
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-lg mb-4">Security isn&apos;t a feature. It&apos;s our foundation.</h2>
          <p className="body-lg max-w-2xl mx-auto text-muted-foreground">
            We built ClearMakes with a &quot;Privacy-First&quot; architecture. We don&apos;t see your data; we just process it.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                className="glass-panel p-6 rounded-2xl border border-white/10"
                initial={{ opacity: 0, y: 25 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-base font-semibold mb-3">{card.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{card.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
